import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import TableSortLabel from '@mui/material/TableSortLabel'
import { Link } from 'react-router-dom'
import { formatMoney, formatPercentage } from '../../packages/utils/numberFormatters'
import { getLevelName, formatFeeLevel } from '../issues/utils'
import { Fee } from '../../types/FeeEntity'
import { InvestorBase } from '../../types/InvestorEntity'

type InvestorWithLevels = {
    id: string
    investorId: string
    name: string
    leo: boolean
    totalAmountInCash: number
    totalAmountInPercentage: number
    levels: Array<{ feeLevel: number; amountInPercentage: number; amountInCash: number }>
}

type SortableKeys = keyof Pick<InvestorWithLevels, 'name' | 'totalAmountInCash'> | 'level_${number}' | 'default'

/**
 * Works with the investors (issue.investors) array and returns a list of objects with the following properties:
 * - name: the name of the investor
 * - leo: flag to indicate if the investor is a Leo
 * - totalAmountInCash: the total amount of the issue the investor is responsible for in cash
 * - totalAmountInPercentage: the total amount of the issue the investor is responsible for in percentage
 * - levels: list of levels the investor is in, where each level is an object with the following properties:
 *  - feeLevel: the level number, starting from 0 (0 is Underwriter)
 *  - amountInPercentage: the amount of the issue the investor is responsible for in percentage
 *  - amountInCash: the amount of the issue the investor is responsible for in cash
 */
function mapInvestorsToNamesAndLevels({
    investors,
    totalOfferingSize,
}: {
    investors: InvestorBase[]
    totalOfferingSize: number
}) {
    // Each investor is an object with the following properties:
    // - name: the name of the investor
    // - leo: flag to indicate if the investor is a Leo
    // - feeLevel: the level number, starting from 0 (0 is Underwriter)
    // - amountInPercentage: the amount of the issue the investor is responsible for in percentage
    // - amountInCash: the amount of the issue the investor is responsible for in cash
    // Each investor can be in multiple levels, so may appear multiple times in the list
    let investorsNotSellers = investors
    // remove sellers from the list of investors
    investorsNotSellers = investorsNotSellers.filter((investor) => !investor.isSeller)
    const investorsWithLevels = investorsNotSellers.reduce(
        (acc, investor) => {
            const { id, investorId, name, leo, feeLevel } = investor
            // the amounts should either be set or calculated from the other amount
            const amountInPercentage =
                investor.amountInPercentage !== null &&
                investor.amountInPercentage !== undefined &&
                isFinite(investor.amountInPercentage)
                    ? investor.amountInPercentage
                    : ((investor.amountInCash || 0) / totalOfferingSize) * 100

            const amountInCash = investor.amountInCash
                ? investor.amountInCash
                : ((investor.amountInPercentage || 0) / 100) * totalOfferingSize
            const result = acc[name] || {
                id,
                investorId,
                name,
                leo,
                totalAmountInCash: 0,
                totalAmountInPercentage: 0,
                levels: [],
            }

            result.totalAmountInCash += amountInCash
            result.totalAmountInPercentage += amountInPercentage
            result.levels.push({
                feeLevel,
                amountInPercentage,
                amountInCash,
            })

            return {
                ...acc,
                [name]: {
                    ...acc[name],
                    ...result,
                },
            }
        },
        {} as Record<string, InvestorWithLevels>,
    )

    return Object.values(investorsWithLevels)
}

const sortInvestors = (
    investors: InvestorWithLevels[],
    orderBy: SortableKeys,
    order: 'asc' | 'desc',
    locale: string,
) => {
    switch (orderBy) {
        case 'name':
            return investors.sort((a, b) => {
                if (order === 'asc') return a.name.localeCompare(b.name, locale)

                return b.name.localeCompare(a.name, locale)
            })
        case 'totalAmountInCash':
            return investors.sort((a, b) => {
                if (order === 'asc') return a.totalAmountInCash - b.totalAmountInCash

                return b.totalAmountInCash - a.totalAmountInCash
            })
        case 'default': {
            // sort the rows by the amount in level 0 (Underwriter) in descending order, then in level 1, etc.
            const maxFeeLevel = Math.max(
                ...investors.map((row) => Math.max(...row.levels.map((level) => level.feeLevel))),
            )
            for (let feeLevelToSort = maxFeeLevel; feeLevelToSort >= 0; feeLevelToSort--) {
                investors = investors.sort((a, b) => {
                    const aLevel0 = a.levels.find((l) => l.feeLevel === feeLevelToSort)
                    const bLevel0 = b.levels.find((l) => l.feeLevel === feeLevelToSort)
                    if (aLevel0 && bLevel0) {
                        if (bLevel0.amountInCash === aLevel0.amountInCash) {
                            return a.name.localeCompare(b.name)
                        }
                        return bLevel0.amountInCash - aLevel0.amountInCash
                    }

                    if (aLevel0 && !bLevel0) {
                        return -1
                    }
                    if (!aLevel0 && bLevel0) {
                        return 1
                    }

                    return 0
                })
            }
            return investors
        }
        default: {
            if (!orderBy.startsWith('level_')) return investors
            const [, level] = orderBy.split('level_')

            return investors.sort((a, b) => {
                const getSumByLevel = ({ levels }: InvestorWithLevels) =>
                    levels.filter((l) => l.feeLevel === +level).reduce((acc, val) => acc + val.amountInCash, 0)

                if (order === 'asc') return getSumByLevel(a) - getSumByLevel(b)

                return getSumByLevel(b) - getSumByLevel(a)
            })
        }
    }
}

function UnderwritersAndGuarantorsTable({
    currency,
    fees,
    investors,
    isUnit,
    noGuarantors,
    totalOfferingSize,
    hideTotalColumns,
}: {
    currency: string | null
    fees: Fee[]
    investors: InvestorBase[]
    isUnit: boolean
    noGuarantors?: boolean
    totalOfferingSize: number
    hideTotalColumns?: boolean
}) {
    const { i18n } = useTranslation()
    const locale = i18n.language
    const [order, setOrder] = useState<'asc' | 'desc'>('desc')
    const [orderBy, setOrderBy] = useState<SortableKeys>('default')

    const investorsRows = sortInvestors(
        mapInvestorsToNamesAndLevels({ investors, totalOfferingSize }),
        orderBy,
        order,
        locale,
    )

    const maxFeeLevel = Math.max(...investorsRows.map((row) => Math.max(...row.levels.map((level) => level.feeLevel))))

    const amountNotDefined = investorsRows.reduce((acc, investor) => {
        investor.levels.forEach((level) => {
            if (level.amountInCash === 0) {
                acc = true
            }
        })
        return acc
    }, false)

    const createSortHandler = (property: SortableKeys) => () => {
        let newOrder: 'asc' | 'desc'
        let isDefault: boolean

        if (property === 'name') {
            newOrder = orderBy === property && order === 'asc' ? 'desc' : 'asc'
            isDefault = orderBy === property && order === 'desc' // Third click will reset the order to default
        } else {
            newOrder = orderBy === property && order === 'desc' ? 'asc' : 'desc'
            isDefault = orderBy === property && order === 'asc' // Third click will reset the order to default
        }

        setOrder(newOrder)
        setOrderBy(isDefault ? 'default' : property)
    }

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <TableSortLabel
                                active={orderBy === 'name'}
                                direction={orderBy === 'name' ? order : 'asc'}
                                onClick={createSortHandler('name')}
                            >
                                Namn
                            </TableSortLabel>
                        </TableCell>
                        {Array.from({ length: maxFeeLevel + 1 }, (_, i) => i).map((level) => (
                            <TableCell key={level}>
                                <TableSortLabel
                                    key={`level_` + level}
                                    active={orderBy === 'level_' + level}
                                    direction={orderBy === 'level_' + level ? order : 'asc'}
                                    onClick={createSortHandler(('level_' + level) as 'level_${number}')}
                                >
                                    {getLevelName(level, maxFeeLevel)}
                                </TableSortLabel>
                            </TableCell>
                        ))}
                        {!hideTotalColumns && [
                            <TableCell key='cash'>
                                <TableSortLabel
                                    active={orderBy === 'totalAmountInCash'}
                                    direction={orderBy === 'totalAmountInCash' ? order : 'asc'}
                                    onClick={createSortHandler('totalAmountInCash')}
                                >
                                    Totalt ({currency})
                                </TableSortLabel>
                            </TableCell>,
                            <TableCell key='percentage'>Totalt i %</TableCell>,
                        ]}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {investorsRows.map((row, index) => (
                        <TableRow key={index}>
                            <TableCell>
                                {row.name === '?' ? (
                                    <Typography variant='body3'>Ej namngiven</Typography>
                                ) : (
                                    <Link to={`/investors/${row.investorId}`}>{row.name}</Link>
                                )}
                            </TableCell>
                            {Array.from({ length: maxFeeLevel + 1 }, (_, i) => i).map((level) => {
                                const levelRow = row.levels.find((l) => l.feeLevel === level)

                                if (!levelRow) {
                                    return <TableCell key={level} />
                                }

                                return (
                                    <TableCell key={level}>
                                        {levelRow.amountInCash > 0
                                            ? formatMoney(levelRow.amountInCash, {
                                                  decimals: 0,
                                                  locale,
                                              })
                                            : '*'}
                                    </TableCell>
                                )
                            })}
                            {!hideTotalColumns && [
                                <TableCell key='cash'>
                                    {row.totalAmountInCash > 0
                                        ? formatMoney(row.totalAmountInCash, {
                                              decimals: 0,
                                              locale,
                                          })
                                        : null}
                                </TableCell>,
                                <TableCell key='percentage'>
                                    {row.totalAmountInPercentage > 0
                                        ? formatPercentage(row.totalAmountInPercentage, {
                                              decimals: 1,
                                              locale,
                                          })
                                        : null}
                                </TableCell>,
                            ]}
                        </TableRow>
                    ))}
                    <TableRow>
                        <TableCell>
                            <b>Summa</b>
                        </TableCell>
                        {Array.from({ length: maxFeeLevel + 1 }, (_, i) => i).map((level) => (
                            <TableCell key={level}>
                                <b>
                                    {formatMoney(
                                        investorsRows.reduce(
                                            (acc, row) =>
                                                acc +
                                                (row.levels.find((l) => l.feeLevel === level)
                                                    ? row.levels.find((l) => l.feeLevel === level).amountInCash
                                                    : 0),
                                            0,
                                        ),
                                        { decimals: 0, locale },
                                    )}
                                </b>
                            </TableCell>
                        ))}
                        {!hideTotalColumns && [
                            <TableCell key='cash'>
                                <b>
                                    {formatMoney(
                                        investorsRows.reduce((acc, row) => acc + row.totalAmountInCash, 0),
                                        { decimals: 0, locale },
                                    )}
                                </b>
                            </TableCell>,
                            <TableCell key='percentage'>
                                <b>
                                    {formatPercentage(
                                        investorsRows.reduce((acc, row) => acc + row.totalAmountInPercentage, 0),
                                        { decimals: 1, locale },
                                    )}
                                </b>
                            </TableCell>,
                        ]}
                    </TableRow>
                    {!noGuarantors && investorsRows.length > 0 && (
                        <TableRow>
                            <TableCell>
                                <Typography variant='caption'>Ersättningsnivå</Typography>
                            </TableCell>
                            <TableCell />
                            {Array.from({ length: maxFeeLevel + (hideTotalColumns ? 0 : 2) }, (_, i) => i).map(
                                (level) => (
                                    <TableCell key={level}>
                                        <Typography variant='caption' style={{ whiteSpace: 'pre-line' }}>
                                            {formatFeeLevel({ fees, isUnit }, level, maxFeeLevel, locale)}
                                        </Typography>
                                    </TableCell>
                                ),
                            )}
                        </TableRow>
                    )}
                    {amountNotDefined ? (
                        <TableRow>
                            <TableCell colSpan={maxFeeLevel + 4}>
                                <Typography variant='caption'>
                                    *
                                    <i>
                                        Fullständig information angående belopp per deltagare har ej kommunicerats av
                                        bolaget.
                                    </i>
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : null}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default UnderwritersAndGuarantorsTable
